import { faCalculator, faPlus } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useGetConsignmentRules from '../../../api/consignment-rules/useGetConsignmentRules';
import useCreateConsignmentRule from '../../../api/consignment-rules/useCreateConsignmentRule';
import useUpdateConsignmentRule from '../../../api/consignment-rules/useUpdateConsignmentRule';
import useGetPackaging from '../../../api/packaging/useGetPackaging';
import GenericPageContainer from '../../../components/agv-generic-page-container';
import SearchFilter from '../../../components/agv-header-search';
import GenericTable from '../../../components/agv-generic-table';
import GenericTableSkeleton from '../../../components/agv-generic-table/genericTableSkeleton';
import EmptyList from '../../../components/agv-empty-list';
import AgrivitechPagination from '../../../components/agv-pagination';
import { STATUS_ENUM } from '../../../components/agv-status/status-enum';
import HeaderButton from '../../../components/agv-header-button';
import {
  CONSIGNMENT_RULE_TYPE_LABEL,
  formatConsignmentRuleParams,
} from '../../../utils/consignment-rule.util';
import ConsignmentRuleCreateModal from './consignment-rule-create-modal';
import ConsignmentRuleUpdateModal from './consignment-rule-update-modal';

export default function ConsignmentRules() {
  const [rule, setRule] = useState(null);
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    limit: 10,
    offset: searchParams.get('offset') || 0,
    item_key: searchParams.get('item_key') || '',
  });

  const packagingFilters = useMemo(
    () => ({ limit: 200, offset: 0, query: '' }),
    []
  );

  const handleUpdateParam = useCallback((key, value) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, [key]: value };
      if (key !== 'offset') {
        newFilters.offset = 0;
      }
      return newFilters;
    });
  }, []);

  const { data, isLoading, mutate } = useGetConsignmentRules(filters, 300);
  const { data: packagingData } = useGetPackaging(packagingFilters, 0);

  const {
    createConsignmentRule,
    isLoading: isLoadingCreate,
    isSuccess: isSuccessCreate,
  } = useCreateConsignmentRule();

  const {
    updateConsignmentRule,
    isLoading: isLoadingUpdate,
    isSuccess: isSuccessUpdate,
  } = useUpdateConsignmentRule();

  const packagingOptions = useMemo(() => {
    const items = packagingData?.items ?? [];
    const currentPackagingId = isOpenUpdate ? rule?.packaging_id : null;

    return items
      .filter((item) => item.is_active || item.id === currentPackagingId)
      .map((item) => ({
        value: item.id,
        name: `${item.label} (${item.code})`,
      }));
  }, [packagingData, rule, isOpenUpdate]);

  useEffect(() => {
    const params = new URLSearchParams(filters);
    navigate({ search: params.toString() }, { replace: true });
  }, [filters, navigate]);

  useEffect(() => {
    if (isSuccessCreate) {
      setIsOpenCreate(false);
      mutate();
    }
  }, [isSuccessCreate, mutate]);

  useEffect(() => {
    if (isSuccessUpdate) {
      setIsOpenUpdate(false);
      mutate();
    }
  }, [isSuccessUpdate, mutate]);

  const handleRuleFields = (item) => [
    {
      name: 'Type de règle',
      value: CONSIGNMENT_RULE_TYPE_LABEL[item?.rule_type] || '-',
      widthClass: 'tw-w-[140px]',
    },
    {
      name: 'Palette',
      value: item?.packaging_label
        ? `${item.packaging_label} (${item.packaging_code})`
        : item?.packaging_code || '-',
      widthClass: 'tw-w-[240px]',
    },
    {
      name: 'Mode de calcul',
      value: formatConsignmentRuleParams(item),
      widthClass: 'tw-w-[200px]',
    },
  ];

  const onOpenModal = (value) => {
    setRule(value);
    setIsOpenUpdate(true);
  };

  return (
    <>
      <GenericPageContainer
        title={'Palettes consignées - Règles de consigne'}
        subtitle={
          'Associez une référence produit à un type de palette et définissez la règle de calcul des palettes attendues.'
        }
        filters={
          <>
            <SearchFilter
              value={filters.item_key}
              setValue={(value) => handleUpdateParam('item_key', value)}
              disabled={isLoading}
            />
            <HeaderButton
              onClick={() => setIsOpenCreate(true)}
              text={'Créer'}
              icon={faPlus}
            />
          </>
        }
      >
        {isLoading ? (
          <GenericTableSkeleton numberOfRows={10} hasTitle />
        ) : data?.items?.length > 0 ? (
          <GenericTable
            isLoading={isLoading}
            rows={data.items.map((element) => ({
              title: element.item_key || 'Sans référence',
              subtitle:
                CONSIGNMENT_RULE_TYPE_LABEL[element.rule_type] ||
                element.rule_type,
              titleWidthClass: 'tw-w-[260px]',
              icon: faCalculator,
              onClick: () => onOpenModal(element),
              buttonText: 'Détails',
              status: element?.is_active
                ? STATUS_ENUM.POSITIVE
                : STATUS_ENUM.NEGATIVE,
              statusText: element?.is_active ? 'Actif' : 'Inactif',
              fields: handleRuleFields(element),
            }))}
            iconColor={'tw-text-checklist-common'}
          />
        ) : (
          <EmptyList />
        )}
        <AgrivitechPagination
          data={data}
          setOffset={(value) => handleUpdateParam('offset', value)}
        />
      </GenericPageContainer>

      <ConsignmentRuleCreateModal
        isOpen={isOpenCreate}
        setIsOpen={setIsOpenCreate}
        createConsignmentRule={createConsignmentRule}
        mainActionIsLoading={isLoadingCreate}
        packagingOptions={packagingOptions}
      />

      <ConsignmentRuleUpdateModal
        isOpen={isOpenUpdate}
        setIsOpen={setIsOpenUpdate}
        updateConsignmentRule={updateConsignmentRule}
        mainActionIsLoading={isLoadingUpdate}
        rule={rule}
        packagingOptions={packagingOptions}
      />
    </>
  );
}
